import { useCallback, useEffect, useRef, useState } from "react";
import {
  NEWSLETTER_POPUP_DISMISS_TTL_MS,
  NEWSLETTER_POPUP_SESSION_STORAGE_KEY,
  NEWSLETTER_POPUP_STATE_STORAGE_KEY,
  isNewsletterPopupAutoSuppressed,
  markNewsletterPopupDismissed,
} from "./newsletterPopupRecurrence.js";

export const NEWSLETTER_POPUP_SCROLL_THRESHOLD_PX = 520;

const getPositiveNumber = (value, fallback = 0) => {
  if (!Number.isFinite(value)) {
    return fallback;
  }

  return Math.max(0, value);
};

export const shouldTriggerNewsletterPopup = ({
  scrollY = 0,
  viewportHeight = 0,
  documentHeight = 0,
  scrollThresholdPx = NEWSLETTER_POPUP_SCROLL_THRESHOLD_PX,
} = {}) => {
  const normalizedScrollY = getPositiveNumber(scrollY);
  const normalizedViewportHeight = getPositiveNumber(viewportHeight);
  const normalizedDocumentHeight = getPositiveNumber(documentHeight);
  const normalizedThreshold = getPositiveNumber(
    scrollThresholdPx,
    NEWSLETTER_POPUP_SCROLL_THRESHOLD_PX,
  );
  const maxScrollableY = Math.max(
    0,
    normalizedDocumentHeight - normalizedViewportHeight,
  );

  if (maxScrollableY <= 0) {
    return false;
  }

  return normalizedScrollY >= Math.min(normalizedThreshold, maxScrollableY);
};

const getCurrentViewportState = () => {
  const documentElement = document.documentElement;
  const body = document.body;

  return {
    scrollY: window.scrollY || window.pageYOffset || 0,
    viewportHeight: window.innerHeight || documentElement?.clientHeight || 0,
    documentHeight: Math.max(
      documentElement?.scrollHeight || 0,
      body?.scrollHeight || 0,
    ),
  };
};

function useNewsletterPopupTrigger({
  enabled = true,
  isRegisteredUser = false,
  scrollThresholdPx = NEWSLETTER_POPUP_SCROLL_THRESHOLD_PX,
  dismissTtlMs = NEWSLETTER_POPUP_DISMISS_TTL_MS,
  stateStorageKey = NEWSLETTER_POPUP_STATE_STORAGE_KEY,
  sessionStorageKey = NEWSLETTER_POPUP_SESSION_STORAGE_KEY,
} = {}) {
  const [isNewsletterPopupOpen, setIsNewsletterPopupOpen] = useState(false);
  const hasTriggeredRef = useRef(false);

  const isAutoSuppressed = useCallback(
    () =>
      isNewsletterPopupAutoSuppressed({
        dismissTtlMs,
        stateStorageKey,
        sessionStorageKey,
      }),
    [dismissTtlMs, sessionStorageKey, stateStorageKey],
  );

  const openNewsletterPopup = useCallback(() => {
    if (hasTriggeredRef.current) {
      return;
    }

    hasTriggeredRef.current = true;
    setIsNewsletterPopupOpen(true);
  }, []);

  const closeNewsletterPopup = useCallback(() => {
    markNewsletterPopupDismissed({ stateStorageKey, sessionStorageKey });
    setIsNewsletterPopupOpen(false);
  }, [sessionStorageKey, stateStorageKey]);

  useEffect(() => {
    if (!enabled || isRegisteredUser) {
      setIsNewsletterPopupOpen(false);
      return undefined;
    }

    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      hasTriggeredRef.current ||
      isAutoSuppressed()
    ) {
      return undefined;
    }

    const handleScroll = () => {
      if (hasTriggeredRef.current || isAutoSuppressed()) {
        return;
      }

      if (
        shouldTriggerNewsletterPopup({
          ...getCurrentViewportState(),
          scrollThresholdPx,
        })
      ) {
        openNewsletterPopup();
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [
    enabled,
    isRegisteredUser,
    isAutoSuppressed,
    openNewsletterPopup,
    scrollThresholdPx,
  ]);

  return {
    isNewsletterPopupOpen,
    closeNewsletterPopup,
  };
}

export default useNewsletterPopupTrigger;
